import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  CreatePaymentRoutePayload,
  ListPaymentRoutesParams,
  ProviderRailRoute,
} from "../types/paymentRoutes.types";
import { PAYMENTS_CONFIG_MAX_LIMIT, toQuery, type PaginatedResponse } from "./paymentsConfig.http";

/**
 * Provider routes of the payments runtime — coloca-nest `InternalPaymentRoutesController`
 * (`payment-config.internal.controller.ts`, tag "🛣️ Payments — Provider Routes (Internal)"). Every endpoint
 * is `userTypes: [INTERNAL]`.
 *
 * The full surface is exactly three operations — there is **no PUT/PATCH, no GET by id and no
 * activate/deactivate**:
 *
 * - `GET /internal/payments/routes` (`payments:read`) — paginated (`skip`/`limit`, default 10, **max 200**),
 *   ordered `created_at DESC` (no sort param). Server-side filters: `rail`, `country`, `currency`,
 *   `direction`, `provider_code`, `is_active`. No free-text search, no date range. Soft-deleted rows are
 *   excluded by TypeORM.
 * - `POST /internal/payments/routes` (`payments:write`) — one route per corridor. The partial-unique index
 *   `uniq_provider_rail_routes_corridor (rail, country, currency, direction) WHERE deleted_at IS NULL`
 *   **ignores `is_active`**, so a duplicate — active or not — is **400 `errors.payment.route_exists`**.
 * - `DELETE /internal/payments/routes/:id` (`payments:write`) — soft delete, 204. **404
 *   `errors.payment.route_not_found`** when the id is unknown; a non-UUID id is a 400 (`ParseUUIDPipe`).
 *
 * Re-pointing a corridor at another provider is therefore DELETE-then-POST, as the endpoint's own Swagger
 * description states. `SwitchProviderDialog` owns that sequence and its recovery.
 *
 * RBAC nuance shared with the rest of the runtime surfaces: a missing permission surfaces as **401** (the
 * guard throws Unauthorized) and a wrong `user_type` as **403** — both must latch as no-access via
 * `HttpApiService.isAccessDeniedError`.
 */

const ROUTES_URL = "/internal/payments/routes";

export const PaymentRoutesService = {
  async list(params: ListPaymentRoutesParams = {}): Promise<PaginatedResponse<ProviderRailRoute>> {
    const { data } = await HttpApiService.get<PaginatedResponse<ProviderRailRoute>>(
      `${ROUTES_URL}${toQuery({ ...params })}`,
    );
    return data;
  },

  /**
   * Pages through the catalog at the backend maximum, **without an `is_active` filter**: an inactive row
   * still occupies its corridor, so leaving it out would render that corridor as "no route" and offer a
   * create that the unique index refuses. Coverage is computed from the loaded set, so a partial read must be
   * disclosed (`truncated`) rather than shown as a complete picture.
   */
  async listAll(
    params: Omit<ListPaymentRoutesParams, "skip" | "limit"> = {},
    maxPages = 5,
  ): Promise<{ routes: ProviderRailRoute[]; total: number; truncated: boolean }> {
    const routes: ProviderRailRoute[] = [];
    let skip = 0;
    let total = 0;

    for (let page = 0; page < maxPages; page += 1) {
      const response = await PaymentRoutesService.list({
        ...params,
        skip,
        limit: PAYMENTS_CONFIG_MAX_LIMIT,
      });
      routes.push(...response.data);
      total = response.total;
      if (!response.hasMore || response.data.length === 0) {
        return { routes, total, truncated: false };
      }
      skip += PAYMENTS_CONFIG_MAX_LIMIT;
    }
    return { routes, total, truncated: routes.length < total };
  },

  /**
   * Only the active routes — what the price book needs as its corridor catalog, mirroring
   * `ProviderSelectorService.select`, which matches on `is_active: true`.
   */
  async listActive(maxPages = 5): Promise<{ routes: ProviderRailRoute[]; truncated: boolean }> {
    const { routes, truncated } = await PaymentRoutesService.listAll({ is_active: true }, maxPages);
    return { routes, truncated };
  },

  async create(payload: CreatePaymentRoutePayload): Promise<ProviderRailRoute> {
    const { data } = await HttpApiService.post<ProviderRailRoute, CreatePaymentRoutePayload>(
      ROUTES_URL,
      payload,
    );
    return data;
  },

  async remove(id: string): Promise<void> {
    await HttpApiService.delete(`${ROUTES_URL}/${id}`);
  },
};
